import React from 'react';

import { ASK_AI_BULK } from '@lumapps/ask-ai-bulk/keys';
import { askAiBulkAdminRoute } from '@lumapps/ask-ai-bulk/routes';
import { ASK_AI_CONCEPTS } from '@lumapps/ask-ai-concepts/keys';
import { askAiConceptsAdminRoute } from '@lumapps/ask-ai-concepts/routes';
import { ASK_AI_CONFIGURATIONS } from '@lumapps/ask-ai-configurations/keys';
import { askAiConfigurationsAdminRoute } from '@lumapps/ask-ai-configurations/routes';
import { ASK_AI_RULES } from '@lumapps/ask-ai-rules/keys';
import { askAiBaseRulesAdminRoute } from '@lumapps/ask-ai-rules/routes';
import { askAiBaseSourcesAdminRoute } from '@lumapps/ask-ai-sources/routes';
import { canAccessAskAiAdmin, canAccessAskAiAdminAlphaFeatures } from '@lumapps/ask-ai/ducks/selectors';
import { SideNavigationItem, type SideNavigationItemProps } from '@lumapps/lumx-back-office/components/SideNavigation';
import { mdiSparkles } from '@lumapps/lumx/icons';
import { useSelector } from '@lumapps/redux/react';
import { GLOBAL, useTranslate } from '@lumapps/translations';

export type SearchAiSideNavigationProps = Partial<SideNavigationItemProps> & {
    id: string;
};

/**
 * Side navigation section for the Ask AI feature.
 */
export const SearchAiSideNavigation: React.FC<SearchAiSideNavigationProps> = ({ id, ...restOfProps }) => {
    const { translateKey } = useTranslate();

    const isAskAiEnabled = useSelector(canAccessAskAiAdmin);
    const isAskAiAlphaEnabled = useSelector(canAccessAskAiAdminAlphaFeatures);

    if (!isAskAiEnabled) {
        return null;
    }

    return (
        <SideNavigationItem {...restOfProps} label={translateKey(GLOBAL.ASK_AI)} icon={mdiSparkles} id={id}>
            <SideNavigationItem
                route={askAiConfigurationsAdminRoute}
                label={translateKey(ASK_AI_CONFIGURATIONS.CONFIGURATIONS)}
                id={`${id}--configurations`}
            />
            <SideNavigationItem
                route={askAiBaseRulesAdminRoute}
                label={translateKey(ASK_AI_RULES.RULES)}
                id={`${id}--rules`}
            />
            <SideNavigationItem
                route={askAiConceptsAdminRoute}
                label={translateKey(ASK_AI_CONCEPTS.CONCEPTS)}
                id={`${id}--concepts`}
            />
            <SideNavigationItem
                route={askAiBaseSourcesAdminRoute}
                label={translateKey(GLOBAL.SOURCES)}
                id={`${id}--sources`}
            />
            {isAskAiAlphaEnabled && (
                <SideNavigationItem
                    route={askAiBulkAdminRoute}
                    label={translateKey(ASK_AI_BULK.BULK)}
                    id={`${id}--bulk`}
                />
            )}
        </SideNavigationItem>
    );
};
